import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Autoplay, Navigation, Pagination, Scrollbar, A11y } from 'swiper';
import { isWithinValidLife, isWithinSchedule } from './Helpers/DateHelper';
import { Carousel, Button, Fade, Collapse, Container } from 'react-bootstrap';
import FadeIn from 'react-fade-in/lib/FadeIn';
import BannerHeroSkeleton from './components/BannerHeroSkeleton.js';
import WithErrorBoundary from '../../HOCs/WithErrorBoundary';
import './styles.scss';
import * as hooks from './hooks';
import { IMAGE_CROP_MODES } from '../shared/constants';
import { IMAGE_MODE } from '../../constants/imageMode';
import BannerHeroPagination from './components/BannerHeroPagination';
import TrailerVideo from './components/TrailerVideo';
import BannerHeroCaption from './components/BannerHeroCaption';

const { Caption } = Carousel;

// install Swiper components
SwiperCore.use([Autoplay, Navigation, Pagination, Scrollbar, A11y]);


function BannerHero({
  contentCollection,
  isLoading,
  contentNoImageProps,
  redirectHandler = (c) => alert('redirect in BannerHero'),
  onClickPlayerButton = (c) => alert('play in BannerHero'),
  translationTexts,
  showProps,
  selectedContentHandler = (c) => alert('content in BannerHero'),
}) {
  const [items, setItems] = React.useState([]);
  const [timeoutID, setTimeoutID] = React.useState(null);
  const [swiperState, setSwiperState] = React.useState(null);
  const [hoverActive, setHoverActive] = React.useState(false);
  const [muted, setMuted] = React.useState(true);

  React.useEffect(() => {
    if (contentCollection?.length > 0 && !isLoading) {
      const validBannerItems = contentCollection
        ?.filter(
          (bannerItem) =>
            isWithinValidLife(bannerItem.life) &&
            isWithinSchedule(bannerItem.schedule)
        )
        .sort((a, b) => a.order - b.order);
      setItems(validBannerItems)
    }
  }, [contentCollection])


  if (isLoading) return (<FadeIn delay={20} ><BannerHeroSkeleton contentNoImageProps={contentNoImageProps} /></FadeIn>);

  const autoplayOptions = true ? {
    autoplay: {
      delay: 10000,
      disableOnInteraction: false
    }
  } : {};

  const handleOnLeave = () => {
    if (timeoutID) {
      clearTimeout(timeoutID);
      setTimeoutID(null);
    }
    setHoverActive(false);
    swiperState?.autoplay?.start();
    setMuted(true);
  }


  const handleOnMove = () => {
    if (!timeoutID && items[swiperState?.realIndex]?.relatedContent?.embeddedTrailer?.url) {
      swiperState?.autoplay.stop();
      const timeoutIDR = setTimeout(() => {
        setHoverActive(true);
      }, 4000);
      setTimeoutID(timeoutIDR);
    }
  }

  const handleOnvideoEnded = ()=>{
    swiperState?.slideNext();
  }

  // check if the component is within life and within schedule
  if (!items?.length > 0) {
    return null;
  } else {
    return (
      <section className="tbxBanner">
        <Container fluid className="bannerHeroWrapper">
          <div
          >
            <FadeIn delay={20} >
              <Swiper
                {...autoplayOptions}
                zoom={true}
                mousewheel={{
                  forceToAxis: true
                }}
                speed={1000}
                centeredSlides={true}
                className="tbxBanner__container-swiper"
                draggable={false}
                grabCursor={false}
                loop={items.length > 1 ? true : false}
                onSwiper={(swiper) => {
                  setSwiperState(swiper)
                }}
                simulateTouch= {items.length > 1 ? true : false}
                onSlideChangeTransitionEnd={(swiper) => {
                  handleOnLeave();
                  handleOnMove();
                }}
              >
                <span slot="container-start">
                  {items.length > 1 && <BannerHeroPagination swiperState={swiperState} length={items.length} isLoading={isLoading} />}
                </span>
                {items.map((element, indexR) =>
                  <SwiperSlide
                    className="item"
                    key={element.id}
                  >
                    {(element?.images[0]?.url) &&
                      <div
                        className="bannerHeroImage d-block w-100"
                        style={{
                          backgroundImage: `url(${element.images[0].url}?crop=${IMAGE_CROP_MODES.resizeScaleAndCrop}&size=${IMAGE_MODE.THUMB_BANNER_HERO})`,
                          opacity: (!hoverActive || indexR !== swiperState.realIndex) ? 1 : 0
                        }}
                      />
                    }
                    {(element?.relatedContent?.embeddedTrailer?.url && hoverActive && indexR === swiperState.realIndex) &&
                      <TrailerVideo
                        className="bannerHeroVideo d-block w-100"
                        content={element}
                        videoSource={element.relatedContent.embeddedTrailer.url}
                        muted={muted} 
                        handleOnvideoEnded={handleOnvideoEnded}
                        />}
                    {element?.relatedContent?.embeddedTrailer?.url && hoverActive && indexR === swiperState.realIndex && 
                    <Button className="bannerHero_muted" onClick={() => setMuted(!muted)}>
                      {muted ?
                        <i className="bi bi-volume-mute-fill" /> :
                        <i className="bi bi-volume-up-fill" />
                      }
                    </Button>}
                    {<BannerHeroCaption
                      content={element}
                      translationTexts={translationTexts}
                      redirectHandler={redirectHandler}
                      onClickPlayerButton={onClickPlayerButton}
                      showProps={showProps}
                      selectedContentHandler={selectedContentHandler}
                    />}
                  </SwiperSlide>
                )}
              </Swiper>
            </FadeIn>
          </div>
        </Container>
      </section>
    );
  }
};

BannerHero.propTypes = {
  contentCollection: PropTypes.array,
  isLoading: PropTypes.bool,
  selectedContentHandler: PropTypes.func,
  redirectHandler: PropTypes.func,
  translationTexts: PropTypes.object,
  showProps: PropTypes.object
};

export default WithErrorBoundary(React.memo(BannerHero));
